import { inject, Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { RecallOwnerService } from '../recall/recall.service';
import { DealerService } from '../dealer/dealer.service';
import { ICategoryResponse, ICategoryService, IDefaultService, IDefaultServiceOption } from '@core/interface';
import { SessionService } from '../session/session.service';
import { StorageKeys } from '@core/enums';
import { FdsCurrencyPipe } from 'libs/shared/src/lib/project/status/shared/pipes/currency/currency-status.pipe';

@Injectable({
  providedIn: 'root'
})
export class CategoryService {
  readonly #recallOwnerService = inject(RecallOwnerService);
  readonly #dealerService = inject(DealerService);
  readonly #sessionService = inject(SessionService);

  /**
 * Função para obter categorias formatadas
 * @returns Observable<ICategoryService[]>
 */
  /**
   * Recupera uma lista de serviços de categoria.
   *
   * @returns {Observable<ICategoryService[]>} Um observable que emite um array de serviços de categoria.
   *
   * A função realiza as seguintes etapas:
   * 1. Chama `getCategoriesByDealerId` do `#dealerService` para buscar categorias associadas a um dealer.
   * 2. Usa o operador `map` para formatar as categorias buscadas usando o método `#formatCategories`.
   * 3. Usa outro operador `map` para formatar categorias de recall com base na flag `#recallOwnerService.hasRecall`.
   * 4. Usa o operador `tap` para mapear as categorias formatadas por seus IDs usando o método `#mapCategoriesById`.
   */
  get(): Observable<ICategoryService[]> {
    return this.#dealerService.getCategoriesByDealerId().pipe(
      map(categories => this.#formatCategories(categories)),
      map(categories => this.#formatRecallCategory(categories, this.#recallOwnerService.hasRecall)),
      tap(categories => this.#mapCategoriesById(categories)),
    );
  }

  /**
   * Formata as categorias, adicionando informações úteis
   * @param categories Lista de categorias
   * @returns Lista de categorias formatadas
   */
  #formatCategories(categories: ICategoryResponse[]): ICategoryService[] {
    const formattedCategories: ICategoryService[] = categories.map(category => ({
      id: category.id,
      title: category.osrName,
      isRecall: category.recall,
      isMaintenance: category.revision,
      iconImage: category.image?.file ? `data:image/svg+xml;base64,${category.image?.file}` : null,
      description: '',
      hasRecall: false,
      recallWarning: '',
      availableServices: category.availableServices
    }));

    return formattedCategories;
  }

  /**
   * Formata as categorias com informações de recall
   * @param categories Lista de categorias a serem formatadas
   * @param hasRecall Indica se há recalls disponíveis
   * @returns Lista de categorias formatadas
   */
  #formatRecallCategory(categories: ICategoryService[], hasRecall: boolean): ICategoryService[] {
    const recallCategory = categories.find(category => category.isRecall);
    
    if (!recallCategory) return categories;

    Object.assign(recallCategory, hasRecall
      ? { iconImage: './assets/images/icons/Icon_Recall_Balloon.svg', 
        title: recallCategory.title, 
        description: "general-services.button.recall.subtitle-with-recall", 
        hasRecall: true }
      : { iconImage: './assets/images/icons/Icon-check-circle.svg', 
        title: recallCategory.title, 
        description: "general-services.button.recall.description-without-recall", 
        hasRecall: false }
    );

    return categories;
  }


  /**
   * Mapeia as categorias por ID para facilitar o acesso e grava no storage
   * @param list Lista de categorias
   */
  #mapCategoriesById(list: ICategoryService[]) {
    this.#sessionService.setObject(StorageKeys.CATEGORIES_BY_DEALER, {});

    const categories: { [key: number]: ICategoryService } = {};
    for (const item of list) {
      categories[item.id] = item;
    }

    this.#sessionService.setObject(StorageKeys.CATEGORIES_BY_DEALER, categories);
  }

  /**
   * Busca o nome da categoria pelo ID dela
   * @param id Id da categoria desejada
   * @returns O nome da categoria
   */
  getCategoryNameById(id: number): string {
    const categories = this.#sessionService.getObject<{ [key: number]: ICategoryService }>(StorageKeys.CATEGORIES_BY_DEALER);
    return categories[id]?.title || '';
  }
}
